import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

// Connects to data-controller="help-search"
export default class extends Controller {
  static targets = ["modal", "input", "results"]
  static values = {
    searchUrl: String,
    debounce: { type: Number, default: 300 }
  }

  declare modalTarget: HTMLDialogElement
  declare inputTarget: HTMLInputElement
  declare resultsTarget: HTMLElement
  declare searchUrlValue: string
  declare debounceValue: number
  private timeout?: number
  private selectedIndex = -1
  private resultItems: HTMLElement[] = []

  connect() {
    // Set up keyboard shortcuts
    document.addEventListener('keydown', this.handleGlobalKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleGlobalKeydown.bind(this))
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  // Open modal when ⌘K is pressed
  handleGlobalKeydown(event: KeyboardEvent) {
    // Handle modal keyboard navigation
    if (this.modalTarget.open) {
      switch (event.key) {
        case 'Escape':
          event.preventDefault()
          this.closeModal()
          break
        case 'ArrowDown':
          event.preventDefault()
          this.navigateResults(1)
          break
        case 'ArrowUp':
          event.preventDefault()
          this.navigateResults(-1)
          break
        case 'Enter':
          event.preventDefault()
          this.selectResult()
          break
      }
    }
  }

  openModal() {
    this.modalTarget.showModal()
    this.inputTarget.focus()
    this.inputTarget.value = ''
    this.clearResults()
    this.selectedIndex = -1
  }

  closeModal() {
    this.modalTarget.close()
    this.clearResults()
    this.selectedIndex = -1
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    this.timeout = setTimeout(() => {
      if (query.length < 2) {
        this.showEmptyState()
        return
      }

      this.performSearch(query)
    }, this.debounceValue)
  }

  private async performSearch(query: string) {
    try {
      this.showLoadingState()

      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set('q', query)

      const response = await fetch(url.toString(), {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error('Search request failed')
      }

      const data = await response.json()
      this.displayResults(data.results, query)
    } catch (error) {
      console.error('Search error:', error)
      this.showErrorState()
    }
  }

  private showLoadingState() {
    this.resultsTarget.innerHTML = `
      <div class="text-center py-8">
        <div class="loading loading-spinner loading-md text-primary"></div>
        <p class="text-gray-500 mt-2">${i18n.t('help_search.searching')}</p>
      </div>
    `
  }

  private showEmptyState() {
    this.resultsTarget.innerHTML = `
      <div class="text-center py-8 text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
        <p>${i18n.t('help_search.start_typing')}</p>
      </div>
    `
    this.resultItems = []
    this.selectedIndex = -1
  }

  private showErrorState() {
    this.resultsTarget.innerHTML = `
      <div class="text-center py-8 text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-4 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p>${i18n.t('help_search.something_went_wrong')}</p>
      </div>
    `
    this.resultItems = []
    this.selectedIndex = -1
  }

  private displayResults(results: any[], query: string) {
    if (results.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p class="text-lg mb-2">${i18n.t('help_search.no_results_found')}</p>
          <p class="text-sm">${i18n.t('help_search.try_adjusting')}</p>
        </div>
      `
      this.resultItems = []
      this.selectedIndex = -1
      return
    }

    const resultsHtml = results.map((result, index) => `
      <div class="search-result p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-gray-50' : ''}"
           data-index="${index}"
           data-url="${result.url}">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <h4 class="font-medium text-gray-900 mb-1">${this.highlightText(result.title, query)}</h4>
            ${result.excerpt ? `<p class="text-sm text-gray-600 mb-2">${this.highlightText(result.excerpt, query)}</p>` : ''}
            <div class="flex items-center gap-2 text-xs text-gray-500">
              <span class="badge badge-outline badge-xs">${result.category}</span>
              <span>•</span>
              <span>${this.formatDate(result.published_at)}</span>
            </div>
          </div>
          <div class="ml-4">
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </div>
        </div>
      </div>
    `).join('')

    this.resultsTarget.innerHTML = `
      <div class="search-results">
        ${resultsHtml}
      </div>
    `

    // Store result items and set up click handlers
    this.resultItems = Array.from(this.resultsTarget.querySelectorAll('.search-result')) as HTMLElement[]
    this.selectedIndex = 0
    this.updateSelection()

    // Add click handlers
    this.resultItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        this.selectedIndex = index
        this.selectResult()
      })
    })
  }

  private navigateResults(direction: number) {
    if (this.resultItems.length === 0) return

    this.selectedIndex = Math.max(0, Math.min(this.resultItems.length - 1, this.selectedIndex + direction))
    this.updateSelection()
  }

  private updateSelection() {
    this.resultItems.forEach((item, index) => {
      item.classList.toggle('bg-gray-50', index === this.selectedIndex)
    })

    // Scroll selected item into view
    if (this.resultItems[this.selectedIndex]) {
      this.resultItems[this.selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      })
    }
  }

  private selectResult() {
    if (this.selectedIndex >= 0 && this.resultItems[this.selectedIndex]) {
      const url = this.resultItems[this.selectedIndex].dataset.url
      if (url) {
        this.closeModal()
        window.location.href = url
      }
    }
  }

  private clearResults() {
    this.showEmptyState()
  }

  private highlightText(text: string, query: string): string {
    if (!query || query.length < 2) return text

    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi')
    return text.replace(regex, '<mark class="bg-yellow-200 rounded">$1</mark>')
  }

  private escapeRegex(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 1) {
      return i18n.t('help_search.yesterday')
    } else if (diffDays < 7) {
      return i18n.t('help_search.days_ago', { count: diffDays })
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return i18n.t('help_search.weeks_ago', { count: weeks })
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return i18n.t('help_search.months_ago', { count: months })
    } else {
      return date.toLocaleDateString()
    }
  }
}
